import _ from 'lodash';
import React from 'react';
import { Form } from 'semantic-ui-react';
import { ATTRIBUTE_TYPE as ATTRIBUTE_TYPE_ENUMS } from './enums';
import CustomDropdown from '../components/common/CustomDropdown';
import DateTimeComponent from '../components/common/DateTimeComponent';
import GaugeComponent from '../components/common/GaugeComponent';
import AsciiCheck from '../common/Asciicheck';
/* eslint-disable */

export const ATTRIBUTE_TYPE = {
  PICKLIST: ATTRIBUTE_TYPE_ENUMS.PICKLIST.key,
  STRING: ATTRIBUTE_TYPE_ENUMS.STRING.key,
  TEXTFIELD: 'textfield',
  DECIMAL: ATTRIBUTE_TYPE_ENUMS.DECIMAL.key,
  INTEGER: ATTRIBUTE_TYPE_ENUMS.INTEGER.key,
  DATE: ATTRIBUTE_TYPE_ENUMS.DATE.key,
  TIME: ATTRIBUTE_TYPE_ENUMS.TIME.key,
  CHECKBOX: ATTRIBUTE_TYPE_ENUMS.BOOLEAN.key,
  CUSTOM: 'custom',
  DATE_TIME: ATTRIBUTE_TYPE_ENUMS.DATE_TIME.key,
  METADATA_DYNAMIC_LIST: ATTRIBUTE_TYPE_ENUMS.METADATA_DYNAMIC_LIST.key,
  OBJECT: 'object',
  GAUGE: ATTRIBUTE_TYPE_ENUMS.GUAGE.key,
};

export function getDefaultValue(attributeObj) {
  switch (attributeObj.type) {
    case ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST:
    case ATTRIBUTE_TYPE.PICKLIST: {
      return attributeObj.multiple ? [] : '';
    }
    case ATTRIBUTE_TYPE.CHECKBOX: {
      return false;
    }
    case ATTRIBUTE_TYPE.GAUGE: {
      return { ft: '', in: '', qt: '' };
    }
    default: {
      return '';
    }
  }
}

export function mapEntityToFormValues(entity, schema) {
  const formValues = {};
  for (const [attributeName, attributeObj] of Object.entries(schema)) {
    if (typeof attributeObj.path === 'string') {
      formValues[attributeName] = _.get(entity, attributeObj.path) ?? getDefaultValue(attributeObj);
    } else if (typeof attributeObj.path === 'function') {
      formValues[attributeName] = attributeObj.path(entity);
    }
  }
  return formValues;
}

export function mapFormValuesToEntity(formValues, schema, setValueParams = {}) {
  setValueParams = { ...setValueParams, formValues };
  const entity = {};
  Object.keys(schema).forEach((attributeName) => {
    const attributeObj = schema[attributeName];
    if (attributeObj.path && typeof attributeObj.path === 'string') {
      let value;
      if (attributeObj.type === ATTRIBUTE_TYPE.INTEGER) {
        value = isNaN(parseInt(_.get(formValues, attributeName), 10))
          ? null
          : parseInt(_.get(formValues, attributeName), 10);
      } else if (attributeObj.type === ATTRIBUTE_TYPE.DECIMAL) {
        value = isNaN(parseFloat(_.get(formValues, attributeName)))
          ? null
          : parseFloat(_.get(formValues, attributeName));
      } else if (
        attributeObj.type === ATTRIBUTE_TYPE.STRING ||
        attributeObj.type === ATTRIBUTE_TYPE.PICKLIST ||
        attributeObj.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST ||
        attributeObj.type === ATTRIBUTE_TYPE.TIME ||
        attributeObj.type === ATTRIBUTE_TYPE.DATE
      ) {
        value = _.isEmpty(formValues[attributeName]) ? null : formValues[attributeName];
      } else if (attributeObj.type === ATTRIBUTE_TYPE.GAUGE) {
        if (_.isEmpty(formValues[attributeName])) {
          value = { ft: '', in: '', qt: '' };
        } else {
          const { ft, in: inch, qt } = formValues[attributeName];
          // if any value is filled then add default values as 0 otherwise empty string
          value = ft || inch || qt ? { ft: ft || 0, in: inch || 0, qt: qt || 0 } : formValues[attributeName];
        }
      } else if (attributeObj.type === ATTRIBUTE_TYPE.DATE_TIME) {
        value = _.isEmpty(formValues[attributeName])
          ? null
          : _.omit(formValues[attributeName], ['componentName', 'index']);
      } else if (attributeObj.type === ATTRIBUTE_TYPE.CHECKBOX) {
        value = formValues[attributeName] || false;
      } else {
        value = formValues[attributeName];
      }
      _.set(entity, attributeObj.path, value);
    } else if (attributeObj.setValue && typeof attributeObj.setValue === 'function') {
      _.merge(entity, attributeObj.setValue(setValueParams));
    }
  });
  return entity;
}

export const ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING = {
  [ATTRIBUTE_TYPE.PICKLIST]: ({
    name,
    value,
    options,
    placeholder,
    onChange,
    disabled,
    onBlur,
    multiple,
    onSearchChange,
    additionalInfo,
    additionalInfoLabel,
    style,
    selectOnBlur = false,
  }) => {
    return (
      <CustomDropdown
        clearable
        placeholder={placeholder}
        fluid
        search
        selection
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        multiple={multiple}
        onSearchChange={onSearchChange}
        additionalInfo={additionalInfo}
        additionalInfoLabel={additionalInfoLabel}
        style={style}
        selectOnBlur={selectOnBlur}
      />
    );
  },
  [ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST]: ({
    name,
    value,
    options,
    placeholder,
    onChange,
    disabled,
    onBlur,
    multiple,
    onSearchChange,
    additionalInfo,
    additionalInfoLabel,
  }) => {
    return (
      <CustomDropdown
        clearable
        placeholder={placeholder}
        fluid
        search
        selection
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        name={name}
        onBlur={onBlur}
        multiple={multiple}
        onSearchChange={onSearchChange}
        additionalInfo={additionalInfo}
        additionalInfoLabel={additionalInfoLabel}
      />
    );
  },
  [ATTRIBUTE_TYPE.STRING]: ({ name, value, placeholder, onChange, disabled, onBlur, onClick }) => (
    <input
      type="text"
      name={name}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      disabled={disabled}
      onBlur={onBlur}
      onClick={onClick}
    />
  ),

  [ATTRIBUTE_TYPE.TEXTFIELD]: ({ name, value, placeholder, onChange, disabled, onBlur }) => (
    <textarea
      type="text"
      name={name}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      disabled={disabled}
      onBlur={onBlur}
    />
  ),
  [ATTRIBUTE_TYPE.OBJECT]: ({ name, value, placeholder, onChange, disabled, onBlur }) => {
    for (const [key, value] of Object.entries(value)) {
      return (
        <>
          <div>{key}</div>
          <input type="text" name={key} value={value} disabled></input>
        </>
      );
    }
    return null;
  },
  [ATTRIBUTE_TYPE.DECIMAL]: ({ name, value, placeholder, onChange, disabled, onBlur, index, step }) => {
    return (
      <input
        id={`id_${index}`}
        type="number"
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onBlur={onBlur}
        step={step}
      />
    );
  },
  [ATTRIBUTE_TYPE.INTEGER]: ({ name, value, placeholder, onChange, disabled, onBlur }) => (
    <input
      type="number"
      name={name}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onBlur={onBlur}
    />
  ),
  [ATTRIBUTE_TYPE.DATE]: ({ name, value, placeholder, onChange, disabled, onBlur }) => {
    return (
      <input
        type="date"
        name={name}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
        max={'2030-12-31'}
        disabled={disabled}
        onBlur={onBlur}
      />
    );
  },

  [ATTRIBUTE_TYPE.TIME]: ({ name, value, placeholder, onChange, disabled, onBlur, index, step = 60 }) => (
    <input
      type="time"
      step={step}
      name={name}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onBlur={onBlur}
      key={index}
    />
  ),
  [ATTRIBUTE_TYPE.CHECKBOX]: ({ name, value, placeholder, onChange, disabled, onBlur }) => (
    <>
      <input
        type="checkbox"
        name={name}
        id={name}
        checked={value}
        onChange={onChange}
        style={{ marginTop: '3px', marginRight: '5px' }}
        disabled={disabled}
        onBlur={onBlur}
      />
      <label htmlFor={name}>{placeholder}</label>
    </>
  ),
  [ATTRIBUTE_TYPE.DATE_TIME]: ({ name, value, placeholder, onChange, disabled, onBlur }) => (
    <DateTimeComponent
      name={name}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onBlur={onBlur}
    />
  ),
  [ATTRIBUTE_TYPE.GAUGE]: ({ name, value, placeholder, onChange, disabled, onBlur, attributeObj }) => (
    <GaugeComponent
      name={name}
      onChange={onChange}
      placeholder={placeholder}
      value={value}
      disabled={disabled}
      onBlur={onBlur}
      attributeObj={attributeObj}
    />
  ),
};

export function validateMandatoryAttribute(attributeObj, value) {
  const attributeDisplayName = attributeObj.displayName || attributeObj.name;
  switch (attributeObj.type) {
    case ATTRIBUTE_TYPE.DECIMAL:
    case ATTRIBUTE_TYPE.INTEGER: {
      if (value === '' || _.isNil(value)) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      if (isNaN(parseFloat(value))) {
        return {
          validation: false,
          message: `${attributeDisplayName} is invalid`,
        };
      }
      break;
    }
    case ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST:
    case ATTRIBUTE_TYPE.PICKLIST:
    case ATTRIBUTE_TYPE.STRING: {
      if (_.isEmpty(value)) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      break;
    }
    case ATTRIBUTE_TYPE.DATE: {
      const date = new Date(value);
      const dateCheck = new Date('01-01-1970');

      if (_.isEmpty(value)) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      if (!/\d{3}-\d{2}-\d{2}/.test(value)) {
        return {
          validation: false,
          message: `Invalid date format of ${attributeDisplayName}`,
        };
      }
      if (!_.isEmpty(value) && date < dateCheck) {
        return {
          validation: false,
          message: `Invalid date format Date should be greater than 01/01/1970`,
        };
      }
      break;
    }
    case ATTRIBUTE_TYPE.TIME: {
      if (_.isEmpty(value)) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      if (!/\d{2}:\d{2}/.test(value)) {
        return {
          validation: false,
          message: `Invalid time format of ${attributeDisplayName}`,
        };
      }
      break;
    }
    case ATTRIBUTE_TYPE.DATE_TIME: {
      const date = new Date(value.date);
      const dateCheck = new Date('01-01-1970');

      if (_.isEmpty(value) || !value?.date || !value?.time) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      if (!/\d{3}-\d{2}-\d{2}/.test(value.date)) {
        return {
          validation: false,
          message: `Invalid date format of ${attributeDisplayName}`,
        };
      }
      if (!/\d{2}:\d{2}/.test(value.time)) {
        return {
          validation: false,
          message: `Invalid date format of ${attributeDisplayName}`,
        };
      }
      if (!_.isEmpty(value.date) && date < dateCheck) {
        return {
          validation: false,
          message: `Invalid date format Date should be greater than 01/01/1970`,
        };
      }

      break;
    }
    case ATTRIBUTE_TYPE.GAUGE: {
      if (!value?.ft && !value?.in && !value?.qt) {
        return {
          validation: false,
          message: `${attributeDisplayName} is required`,
        };
      }
      if (value?.ft > 9999) {
        return {
          validation: false,
          message: `ft should be less than 10000`,
        };
      }
      if (value?.in > 11) {
        return {
          validation: false,
          message: `in should be less than 12`,
        };
      }
      if (value?.qt > 3) {
        return {
          validation: false,
          message: `qt should be less than 4`,
        };
      }

      break;
    }
  }
  return { validation: true };
}

export const FormField = ({ attributeObj, formik, task, ...props }) => {
  const isMandatory =
    typeof attributeObj?.isMandatory === 'function'
      ? attributeObj.isMandatory({ formValues: formik.values, task })
      : typeof attributeObj.isMandatory === 'boolean'
      ? attributeObj.isMandatory
      : false;

  const attributeOptions =
    (attributeObj.type === ATTRIBUTE_TYPE.PICKLIST || attributeObj.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST) &&
    (attributeObj.options
      ? typeof attributeObj.options === 'function'
        ? attributeObj.options({ formValues: formik.values, task, ...props })
        : attributeObj.options
      : _.get(props, attributeObj.optionsVariableName, { isLoading: false, items: [] }));

  const onChangeFunction = _.get(props.onValueChange, attributeObj.onChange);

  const attributeOnChange =
    attributeObj.type === ATTRIBUTE_TYPE.PICKLIST || attributeObj.type === ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST
      ? (event, data) => {
          formik.setFieldTouched(attributeObj.attributeName, true, false);
          formik.setFieldValue(attributeObj.attributeName, data.value),
            onChangeFunction &&
              onChangeFunction({
                name: attributeObj.attributeName,
                value: data.value,
                attributeObj,
                formik,
                attributeOptions,
              });
        }
      : attributeObj.type === ATTRIBUTE_TYPE.CHECKBOX
      ? (event) => {
          formik.setFieldTouched(attributeObj.attributeName, true, false);
          formik.setFieldValue(attributeObj.attributeName, event.target.checked);
          onChangeFunction &&
            onChangeFunction({ name: attributeObj.attributeName, value: event.target.checked, attributeObj, formik });
        }
      : attributeObj.type === ATTRIBUTE_TYPE.DATE_TIME || attributeObj.type === ATTRIBUTE_TYPE.GAUGE
      ? (data) => {
          formik.setFieldTouched(attributeObj.attributeName, true, false);
          formik.setFieldValue(attributeObj.attributeName, data);
          onChangeFunction && onChangeFunction({ name: attributeObj.attributeName, value: data, attributeObj, formik });
        }
      : (e) => {
          if (attributeObj.type === ATTRIBUTE_TYPE.DATE || attributeObj.type === ATTRIBUTE_TYPE.TIME) {
            formik.handleChange(e);
          } else {
            formik.handleChange(AsciiCheck(e));
          }

          onChangeFunction &&
            onChangeFunction({ name: attributeObj.attributeName, value: e.target.value, attributeObj, formik });
        };
  const isAttributeDisabled =
    typeof attributeObj.disabled === 'function'
      ? attributeObj.disabled({
          formValues: formik.values,
          task,
          loggedInUser: props.loggedInUser,
          selectedTasks: props.selectedTasks,
          ...props,
        })
      : typeof attributeObj.disabled === 'boolean'
      ? attributeObj.disabled
      : false;
  const attributeValue =
    (attributeObj.value &&
      attributeObj.value({
        formValues: formik.values,
        attributeOptions,
        attributeObj,
        task,
        formik,
        ...props,
      })) ||
    _.get(formik.values, attributeObj.attributeName);
  const attributeSelectedOption =
    attributeOptions && !_.isEmpty(attributeValue) && attributeOptions.find
      ? attributeOptions.find((option) => option.value === attributeValue)
      : attributeOptions.items && attributeOptions.items.find((option) => option.value === attributeValue);

  const attributeOnSearch = attributeObj.onSearchChange
    ? (event, data) => {
        _.get(props, attributeObj.onSearchChange) && _.get(props, attributeObj.onSearchChange)({ event, formik });
      }
    : null;
  const additionalInfo = attributeObj.additionalInfo
    ? _.get(props, attributeObj.additionalInfo)
    : attributeSelectedOption &&
      attributeSelectedOption.originalobject &&
      _.get(attributeObj, 'showDescription', false) &&
      attributeSelectedOption.originalobject.description
    ? attributeSelectedOption.originalobject.description
    : null;
  const { additionalInfoLabel } = attributeObj;
  return (
    <>
      {attributeObj.type === ATTRIBUTE_TYPE.CUSTOM ? (
        attributeObj.component({
          task,
          attributeObj,
          formik,
          disabled: isAttributeDisabled,
          ...props,
        })
      ) : (
        <Form.Field>
          <>
            {attributeObj.type !== ATTRIBUTE_TYPE.CHECKBOX && (
              <label>
                {props?.dynamicName || attributeObj.displayName || attributeObj.name}

                {isMandatory && <span className="mandatory-field">*</span>}
              </label>
            )}
            {ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING[attributeObj.type]({
              name: attributeObj.attributeName,
              value: attributeValue,
              options: attributeOptions,
              placeholder: props?.dynamicName || attributeObj.displayName || attributeObj.name,
              onChange: attributeOnChange,
              onBlur:
                attributeObj.type !== ATTRIBUTE_TYPE.PICKLIST ||
                attributeObj.type !== ATTRIBUTE_TYPE.METADATA_DYNAMIC_LIST
                  ? formik.handleBlur
                  : null,
              multiple: attributeObj.multiple,
              disabled: isAttributeDisabled,
              onSearchChange: attributeOnSearch,
              additionalInfo,
              additionalInfoLabel,
              step: attributeObj.step,
              attributeObj,
            })}
          </>
        </Form.Field>
      )}
      {formik.errors[attributeObj.attributeName] &&
        typeof formik.errors[attributeObj.attributeName] !== 'object' &&
        !formik.errors[attributeObj.attributeName]?.includes('warning') && (
          <div className="errorText">*{formik.errors[attributeObj.attributeName]}</div>
        )}
    </>
  );
};

export function isFormAttributeHiddenOrInvalid(attributeObj, hideFunctionParams) {
  const isFormAttributeInvalid = !(
    ATTRIBUTE_TYPE_INPUT_TYPE_MAPPING[attributeObj.type] ||
    (attributeObj.type === ATTRIBUTE_TYPE.CUSTOM && attributeObj.component)
  );

  if (!isFormAttributeInvalid) {
    const isFormHidden = isFormAttributeHidden(attributeObj, hideFunctionParams);
    return isFormHidden;
  }
  return isFormAttributeInvalid;
}

export function isFormAttributeHidden(attributeObj, hideFunctionParams) {
  return typeof attributeObj.hide === 'function'
    ? attributeObj.hide(hideFunctionParams)
    : typeof attributeObj.hide === 'boolean'
    ? attributeObj.hide
    : false;
}
